import { useCallback, useEffect, useState } from 'react';
import type { Participant } from '@sfc/sdk';
import { navigate, usePathname } from './auth/route';
import { DeckScreen } from './deck/DeckScreen';
import { InterestScreen } from './interest/InterestScreen';
import { IdeasScreen } from './ideas/IdeasScreen';
import { MatchesScreen } from './matches/MatchesScreen';
import { ProfileScreen } from './profile/ProfileScreen';
import { ThreadScreen } from './matches/ThreadScreen';
import { useInterest } from './interest/useInterest';
import { useMatches } from './matches/useMatches';

const TABS = [
  { path: '/', label: 'Deck' },
  { path: '/ideas', label: 'Ideas' },
  { path: '/interest', label: 'Interest' },
  { path: '/matches', label: 'Matches' },
] as const;

/**
 * The three screens somebody signed in moves between.
 *
 * The inbox is fetched here rather than inside the screen that draws it,
 * because the number of people waiting is also the badge on the navigation:
 * fetched twice it would disagree with itself the moment anybody was
 * accepted. The deck and the matches each own their own loading, since
 * nothing outside them reads it.
 */
export function SignedIn({
  participant,
  onSignOut,
}: {
  participant: Participant;
  onSignOut: () => Promise<void>;
}): React.JSX.Element {
  const path = usePathname();
  const inbox = useInterest(true);
  const threads = useMatches();
  const waiting = inbox.state.status === 'ready' ? inbox.state.waiting : 0;

  // Opening a section is a request to see what is in it now, not what was in
  // it when the app was loaded.
  const { refresh: refreshInbox } = inbox;
  const { refresh: refreshMatches } = threads;
  useEffect(() => {
    if (path === '/interest') refreshInbox();
    if (path.startsWith('/matches')) refreshMatches();
  }, [path, refreshInbox, refreshMatches]);

  const openThread = path.startsWith('/matches/') ? path.slice('/matches/'.length) : undefined;

  /*
   * The deck is rebuilt from scratch whenever anything it reads changes.
   *
   * It ranks by the viewer's skills and is filled by other people's ideas, so
   * saving a profile or withdrawing an idea makes whatever is behind the first
   * tab wrong. Remounting it is cheap -- it is one request -- and far less
   * work than teaching it which of the other four screens to listen to.
   */
  const [deckKey, setDeckKey] = useState(0);
  const freshen = useCallback(() => {
    setDeckKey((previous) => previous + 1);
    refreshInbox();
    refreshMatches();
  }, [refreshInbox, refreshMatches]);

  return (
    <div className="app">
      <header className="bar">
        <span className="mark mark--small" aria-hidden="true">
          SFC
        </span>
        {/* The name is the way into the profile, which is where everybody
            looks for it, and it keeps the tab bar to four at phone width. */}
        <button
          className="bar__who bar__who--link"
          type="button"
          data-testid="who"
          aria-current={path === '/profile' ? 'page' : undefined}
          onClick={() => navigate('/profile')}
        >
          {participant.displayName}
        </button>
        <button className="button button--quiet" type="button" onClick={() => void onSignOut()}>
          Sign out
        </button>
      </header>

      <main className="body">
        {path === '/interest' ? (
          <InterestScreen
            state={inbox.state}
            answer={inbox.answer}
            onSeeMatches={() => navigate('/matches')}
          />
        ) : openThread ? (
          <ThreadScreen
            matchId={openThread}
            onBack={() => navigate('/matches')}
            onChanged={refreshMatches}
          />
        ) : path === '/matches' ? (
          <MatchesScreen state={threads.state} onOpen={(id) => navigate(`/matches/${id}`)} />
        ) : path === '/ideas' ? (
          <IdeasScreen onChanged={freshen} onSeeInterest={() => navigate('/interest')} />
        ) : path === '/profile' ? (
          <ProfileScreen onSaved={freshen} />
        ) : (
          <DeckScreen key={deckKey} onChooseSkills={() => navigate('/profile')} />
        )}
      </main>

      <nav className="tabs" aria-label="Sections">
        {TABS.map((tab) => {
          const here = tab.path === '/' ? path === '/' : path.startsWith(tab.path);
          return (
            <button
              key={tab.path}
              className={`tab${here ? ' tab--here' : ''}`}
              type="button"
              // `page` rather than `true`: this is which section of the site
              // you are on, which is what a screen reader should say.
              aria-current={here ? 'page' : undefined}
              data-testid={`tab-${tab.label.toLowerCase()}`}
              onClick={() => navigate(tab.path)}
            >
              {tab.label}
              {tab.path === '/interest' && waiting > 0 && (
                <span className="tab__badge" data-testid="waiting-badge">
                  {waiting}
                  <span className="sr-only"> waiting</span>
                </span>
              )}
              {tab.path === '/matches' && threads.unread > 0 && (
                <span className="tab__badge" data-testid="unread-badge">
                  {threads.unread}
                  <span className="sr-only"> unread</span>
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
